import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Field, SelectField } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { ROUNDING } from "@/lib/constants";

const n = (v) => (v === "" ? null : Number(v));

export const BillingSettingsSection = () => {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/settings/billing").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const setTravel = (i, patch) => setF({ ...f, travel_options: f.travel_options.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const save = async () => {
    try {
      setF((await api.put("/settings/billing", f)).data);
      toast.success("Réglages de facturation enregistrés (les interventions existantes gardent leurs tarifs)");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="dt-card max-w-3xl space-y-4 p-6" data-testid="billing-settings">
      <h2 className="text-lg font-semibold text-white">Facturation</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Tarif horaire particuliers (€/h)"><Input type="number" min={0} step="0.01" value={f.rate_particulier} onChange={(e) => setF({ ...f, rate_particulier: Number(e.target.value) })} data-testid="billing-rate-particulier-input" /></Field>
        <Field label="Tarif horaire professionnels (€/h)"><Input type="number" min={0} step="0.01" value={f.rate_professionnel} onChange={(e) => setF({ ...f, rate_professionnel: Number(e.target.value) })} data-testid="billing-rate-professionnel-input" /></Field>
        <Field label="Arrondi du temps facturable" className="sm:col-span-2"><SelectField value={f.rounding} onChange={(v) => setF({ ...f, rounding: v })} options={ROUNDING} testid="billing-rounding-select" /></Field>
      </div>
      <div className="space-y-2 rounded-lg border border-white/10 p-3">
        <label className="flex items-center gap-3 text-sm text-slate-200"><Switch checked={f.vat_enabled} onCheckedChange={(v) => setF({ ...f, vat_enabled: v })} data-testid="billing-vat-switch" />Gérer la TVA (HT / TVA / TTC)</label>
        {f.vat_enabled
          ? <Field label="Taux de TVA (%)"><Input type="number" min={0} max={100} step="0.1" value={f.vat_rate} onChange={(e) => setF({ ...f, vat_rate: Number(e.target.value) })} data-testid="billing-vat-rate-input" /></Field>
          : <p className="text-xs text-slate-400">Franchise en base : les montants sont affichés sans TVA avec la mention « TVA non applicable, article 293 B du CGI ».</p>}
      </div>
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-slate-400">Forfaits de déplacement (vide = sur devis)</p>
        {f.travel_options.map((t, i) => (
          <div key={i} className="flex gap-2">
            <Input value={t.label} onChange={(e) => setTravel(i, { label: e.target.value })} data-testid={`billing-travel-label-${i}`} />
            <Input type="number" min={0} step="0.01" className="w-28" value={t.price ?? ""} onChange={(e) => setTravel(i, { price: n(e.target.value) })} data-testid={`billing-travel-price-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => setF({ ...f, travel_options: f.travel_options.filter((_, j) => j !== i) })} data-testid={`billing-travel-delete-${i}`}><Trash2 className="h-4 w-4 text-red-300" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setF({ ...f, travel_options: [...f.travel_options, { label: "Nouveau forfait", price: null }] })} data-testid="billing-travel-add-button"><Plus className="mr-1 h-4 w-4" />Ajouter un forfait</Button>
      </div>
      <p className="text-xs text-slate-500">Un changement ne modifie jamais les interventions déjà créées : chacune conserve son tarif, son arrondi et ses réglages de TVA.</p>
      <Button onClick={save} className="dt-glow" data-testid="billing-settings-save-button">Enregistrer</Button>
    </section>
  );
};
